import React, { useState, useEffect, useCallback } from 'react';
import { Save, Plus, AlertTriangle, Mail, FileText } from 'lucide-react';
import { getEmailTemplates, updateEmailTemplate, createEmailTemplate } from '../utils/emailTemplatesApi';
import { uploadFileToPublic } from '../utils/fileUpload';
import { S3_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import SubmitNotification from '../components/SubmitNotification';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EmailTemplates = () => {
  const { userSites } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [submitNotification, setSubmitNotification] = useState(null);

  // WYSIWYG Editor Configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'align', 'link', 'image'
  ];

  const [formData, setFormData] = useState({
    template_id: '',
    subject: '',
    banner: '',
    content: '',
    sub_content: '',
    updated_at: ''
  });

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Ensure templates is always an array
  const safeTemplates = Array.isArray(templates) ? templates : [];

  // Handle userSites - it can be an array or an object, get first element if array
  const siteData = Array.isArray(userSites) ? (userSites.length > 0 ? userSites[0] : null) : userSites;

  // Fetch templates from API based on user's site ID
  const fetchTemplates = useCallback(async () => {
    console.log('🔍 fetchTemplates called with userSites:', userSites);
    console.log('🔍 Using siteData:', siteData);
    
    if (!siteData || !siteData.site_id) {
      console.warn('❌ No site ID available for user:', siteData);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('🚀 Fetching email templates for site_id:', siteData.site_id);
      const response = await getEmailTemplates({ site_id: siteData.site_id });
      
      console.log('📡 API Response:', response);
      setApiResponse(response);
      
      if (response.success) {
        let templateData = response.data; 
        
        // Handle different API response structures
        if (response.data && response.data.data) {
          templateData = response.data.data;
        } else if (response.data && response.data.templates) {
          templateData = response.data.templates;
        } else if (response.data && response.data.items) {
          templateData = response.data.items;
        } else if (response.data && response.data.results) {
          templateData = response.data.results;
        } else if (response.data && response.data.list) {
          templateData = response.data.list;
        } else if (response.data && response.data.records) {
          templateData = response.data.records;
        }
        
        console.log('📋 Processed template data:', templateData);
        
        if (Array.isArray(templateData)) {
          // Transform data to ensure template_id field exists (in case API returns 'id' instead)
          const transformedData = templateData.map(template => ({
            ...template,
            template_id: template.template_id || template.id || ''
          }));
          
          console.log('✅ Setting templates from API:', transformedData);
          setTemplates(transformedData);
          
          // Select first template by default if available
          if (transformedData.length > 0 && !selectedTemplate) {
            const firstTemplate = transformedData[0];
            console.log('🎯 Selecting first template:', firstTemplate);
            setSelectedTemplate(firstTemplate);
            setFormData({
              template_id: firstTemplate.template_id || '',
              subject: firstTemplate.subject || '',
              banner: firstTemplate.banner || '',
              content: firstTemplate.content || '',
              sub_content: firstTemplate.sub_content || '',
              updated_at: firstTemplate.updated_at || ''
            });
          }
        } else {
          console.warn('❌ API returned data but it\'s not an array:', templateData);
          setTemplates([]);
          setSelectedTemplate(null);
        }
      } else {
        console.warn('❌ API call failed:', response.error);
        setTemplates([]);
        setSelectedTemplate(null);
      }
    } catch (error) {
      console.warn('❌ API error:', error);
      // Fallback to mock data if API fails
      const mockData = [
        {
          template_id: 'TPL001',
          site_id: siteData?.site_id,
          subject: 'Welcome to Our Survey',
          banner: '',
          content: '<h2>Welcome!</h2><p>Thank you for participating in our survey.</p><p>We value your feedback and look forward to your responses.</p>',
          sub_content: '<p>Best regards,<br>Survey Team</p>',
          updated_at: new Date().toISOString()
        },
        {
          template_id: 'TPL002',
          site_id: siteData?.site_id,
          subject: 'Reminder: Complete Your Survey',
          banner: '',
          content: '<h2>Don\'t Forget!</h2><p>This is a friendly reminder to complete your survey.</p><p>Your input is important to us.</p>',
          sub_content: '<p>Thank you,<br>Survey Team</p>',
          updated_at: new Date().toISOString()
        }
      ];
      
      setApiResponse({ success: false, error: error.message });
      setTemplates(mockData);
      
      // Select first mock template
      if (mockData.length > 0) {
        const firstTemplate = mockData[0];
        console.log('🎯 Selecting first mock template:', firstTemplate);
        setSelectedTemplate(firstTemplate);
        setFormData({
          template_id: firstTemplate.template_id || '',
          subject: firstTemplate.subject || '',
          banner: firstTemplate.banner || '',
          content: firstTemplate.content || '',
          sub_content: firstTemplate.sub_content || '',
          updated_at: firstTemplate.updated_at || ''
        });
      }
    } finally {
      setLoading(false);
    }
  }, [userSites, selectedTemplate]);

  // Fetch templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitNotification({ type: 'loading', message: isCreateMode ? 'Creating email template...' : 'Updating email template...' });
    console.log('🚀 handleSubmit called, isCreateMode:', isCreateMode);
    console.log('📋 Form data:', formData);
    console.log('🎯 Selected template:', selectedTemplate);
    
    try {
      let response;
      if (isCreateMode) {
        const createData = {
          ...formData,
          site_id: siteData?.site_id
        };
        console.log('📤 Creating email template with data:', createData);
        response = await createEmailTemplate(createData);
      } else {
        if (!selectedTemplate || !selectedTemplate.template_id) {
          console.error('❌ No email template available to update');
          setSubmitNotification({ type: 'error', message: 'No email template available to update.' });
          return;
        }
        console.log('📤 Updating email template with ID:', selectedTemplate.template_id);
        response = await updateEmailTemplate(selectedTemplate.template_id, formData);
      }

      console.log('📡 Submit response:', response);

      if (response.success) {
        console.log('✅ Email template operation successful');
        if (isCreateMode) {
          setSubmitNotification({ type: 'success', message: 'Email template created successfully!' });
          await fetchTemplates();
          setIsCreateMode(false);
        } else {
          setSubmitNotification({ type: 'success', message: 'Email template updated successfully!' });
          await fetchTemplates();
        }
      } else {
        console.error('❌ Email template operation failed:', response.error);
        setSubmitNotification({ type: 'error', message: `Failed to ${isCreateMode ? 'create' : 'update'} email template: ${response.error || 'Unknown error'}` });
      }
    } catch (error) {
      console.error('❌ Submit error:', error);
      setSubmitNotification({ type: 'error', message: `Error ${isCreateMode ? 'creating' : 'updating'} email template: ${error.message}` });
    }
  };

  const resetForm = () => {
    setFormData({
      template_id: '',
      subject: '',
      banner: '',
      content: '',
      sub_content: '',
      updated_at: ''
    });
  };

  const handleCreateMode = () => {
    setIsCreateMode(true);
    resetForm();
  };

  const handleSelectTemplate = (template) => {
    setIsCreateMode(false);
    setSelectedTemplate(template);
    setFormData({
      template_id: template.template_id || '',
      subject: template.subject || '',
      banner: template.banner || '',
      content: template.content || '',
      sub_content: template.sub_content || '',
      updated_at: template.updated_at || ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SubmitNotification notification={submitNotification} onDismiss={() => setSubmitNotification(null)} />
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
          <p className="text-gray-600">
            {isCreateMode ? 'Create new email template.' : 'Manage and update email templates.'}
          </p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          {isCreateMode && safeTemplates.length > 0 && (
            <button 
              onClick={() => handleSelectTemplate(safeTemplates[0])}
              className="btn-secondary flex items-center space-x-2"
            >
              <span>Edit Existing</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Template List */}
        

        {/* Template Form */}
        <div className={`${!isCreateMode && safeTemplates.length > 0 ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
          <div className="card">
            {/**<div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-medium text-gray-900">
                {isCreateMode ? 'Create Email Template' : 'Update Email Template'}
              </h3>
          <p className="text-sm text-gray-600">
            {isCreateMode 
              ? 'Fill in the details to create a new email template.'
              : selectedTemplate 
                ? <span>Editing: <span dangerouslySetInnerHTML={{ __html: selectedTemplate.subject }}></span></span>
                : 'Loading email template...'
            }
          </p>
              {siteData && siteData.site_id && (
                <p className="text-xs text-gray-500">Site ID: {siteData.site_id}</p>
              )}
               
            </div> */}
            
            <div className="p-6">
              {(selectedTemplate || isCreateMode) ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Subject *
                      </label>
                      <div className="border border-gray-300 rounded-md">
                        <ReactQuill
                          value={formData.subject}
                          onChange={(value) => setFormData({...formData, subject: value})}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Enter email subject..."
                          style={{ minHeight: '100px' }}
                        />
                      </div>
                    </div>
                    
                    

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Banner Upload
                      </label>
                      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                          <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                            aria-hidden="true"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <div className="flex text-sm text-gray-600">
                             <label
                              htmlFor="banner-upload"
                              className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                            >
                              <span>{uploading ? 'Uploading...' : 'Upload a file'}</span>
                              <input
                                id="banner-upload"
                                name="banner-upload"
                                type="file"
                                className="sr-only"
                                accept="image/*"
                                disabled={uploading}
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    try {
                                      setUploading(true);
                                      setUploadError(null);
                                      console.log('📤 Starting file upload:', file.name);
                                      
                                      const filePath = await uploadFileToPublic(file, 'banners');
                                      console.log('✅ File uploaded successfully:', filePath);
                                      
                                      setFormData({...formData, banner: filePath});
                                    } catch (error) {
                                      console.error('❌ File upload failed:', error);
                                      setUploadError(error.message);
                                      alert('Failed to upload file: ' + error.message);
                                    } finally {
                                      setUploading(false);
                                    }
                                  }
                                }}
                              />
                            </label>
                            <p className="pl-1">or drag and drop</p>
                          </div>
                          <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                          {uploading && (
                            <p className="text-sm text-blue-600">Uploading banner...</p>
                          )}
                          {uploadError && (
                            <p className="text-sm text-red-600">Error: {uploadError}</p>
                          )}
                          {formData.banner && !uploading && (
                            <div className="mt-2">
                              <p className="text-sm text-green-600">✓ Banner uploaded successfully</p>
                              <img src={`${S3_BASE_URL}/${formData.banner}`} alt="Banner preview" className="mt-2 max-h-32 rounded" />

                              {/*<p className="text-xs text-gray-500 truncate">{formData.banner}</p>*/}

                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Content
                      </label>
                      <div className="border border-gray-300 rounded-md">
                        <ReactQuill
                          value={formData.content}
                          onChange={(value) => setFormData({...formData, content: value})}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Enter main email content..."
                          style={{ minHeight: '200px' }}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sub Content
                      </label>
                      <div className="border border-gray-300 rounded-md">
                        <ReactQuill
                          value={formData.sub_content}
                          onChange={(value) => setFormData({...formData, sub_content: value})}
                          modules={quillModules}
                          formats={quillFormats}
                          placeholder="Enter sub content..."
                          style={{ minHeight: '150px' }}
                        />
                      </div>
                    </div>
                  
                  {/* Submit Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={submitNotification?.type === 'loading'}
                      className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="h-4 w-4" />
                      <span>{submitNotification?.type === 'loading' ? 'Saving...' : (isCreateMode ? 'Create Template' : 'Update Template')}</span>
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-500">No email templates found</p>
                  <p className="text-sm text-gray-400 mb-4">
                    {siteData && siteData.site_id 
                      ? `No email templates available for site ID: ${siteData.site_id}`
                      : 'Unable to load email templates - no site ID available'
                    }
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Please refresh the page to retry loading</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplates;

